import React, { useState, useEffect } from "react";
import SleepSummaryStep from "./sleep-summary-step";
import axios from "../../../../../utils/lib/axios";

const MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
];

/** Time with colons e.g. "10:16:25 PM Dec 28, 2025" (Figma style) */
function formatDateTimeColon(dateStr, timeStr) {
    if (!dateStr || !timeStr) return "—";
    const [h = 0, m = 0, s = 0] = timeStr.split(":").map(Number);
    const d = new Date(dateStr);
    d.setHours(h, m, s, 0);
    const hours = d.getHours();
    const ampm = hours >= 12 ? "PM" : "AM";
    const h12 = hours % 12 || 12;
    const timePart = `${String(h12).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}:${String(d.getSeconds()).padStart(2, "0")} ${ampm}`;
    const datePart = `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
    return `${timePart} ${datePart}`;
}

/** Returns { hours, minutes, seconds } for duration display */
function getSleepDurationHMS(startDate, startTime, endDate, endTime) {
    if (!startDate || !startTime || !endDate || !endTime) return null;
    const [sh, sm, ss] = startTime.split(":").map(Number);
    const [eh, em, es] = endTime.split(":").map(Number);
    const start = new Date(startDate);
    start.setHours(sh, sm, ss || 0, 0);
    const end = new Date(endDate);
    end.setHours(eh, em, es || 0, 0);
    let diffMs = end - start;
    if (diffMs < 0) diffMs += 24 * 60 * 60 * 1000;
    const totalSecs = Math.round(diffMs / 1000);
    const hours = Math.floor(totalSecs / 3600);
    const minutes = Math.floor((totalSecs % 3600) / 60);
    const seconds = totalSecs % 60;
    return { hours, minutes, seconds };
}

const DUMMY_DURATION_HMS = { hours: 7, minutes: 15, seconds: 2 };
const DUMMY_START = "10:16:25 PM Dec 28, 2025";
const DUMMY_END = "07:08:04 AM Dec 29, 2025";

const SAMPLE_DROP_EPISODES = [
    {
        alertThreshold: 92,
        rows: [
            {
                time: "11:51:57",
                spo2: 94,
                hr: 69,
                alt: 31,
                hum: 54,
                aqi: 38,
                baroIndex: "30.2",
                patientNotes: "—",
            },
            {
                time: "11:52:00",
                spo2: 93,
                hr: 68,
                alt: 31,
                hum: 54,
                aqi: 38,
                baroIndex: "30.2",
                patientNotes: "—",
            },
            {
                time: "11:52:15",
                spo2: 88,
                hr: 70,
                alt: 31,
                hum: 54,
                aqi: 38,
                baroIndex: "30.2",
                patientNotes: "—",
            },
            {
                time: "11:52:16",
                spo2: 90,
                hr: 69,
                alt: 31,
                hum: 54,
                aqi: 38,
                baroIndex: "30.2",
                patientNotes: "—",
            },
        ],
    },
    {
        alertThreshold: 92,
        rows: [
            {
                time: "12:10:01",
                spo2: 95,
                hr: 65,
                alt: 31,
                hum: 54,
                aqi: 38,
                baroIndex: "30.2",
                patientNotes: "—",
            },
            {
                time: "12:10:15",
                spo2: 91,
                hr: 67,
                alt: 31,
                hum: 54,
                aqi: 38,
                baroIndex: "30.2",
                patientNotes: "—",
            },
        ],
    },
];

// const COMMENT_ERROR = "Please enter the coach comments.";
// const ASSIGN_TO_ERROR = "Please assign to a coach.";
// const COACH_INTERPRETATION_ERROR =
//     "Please enter the coach interpretation.";

export default function ReviewAndGenerateReportWrapper({
    formData,
    devices,
    onBack,
    dropEpisodes,
    onGenerate,
    onStep1Submit,
    reportData,
    setAlertDetails,
    reportId,
    setReportGeneratedSuccessAlert,
    setSleepReportModalOpen,
    loadWaitingData,
    onSleepReportGenerated,
}) {
    // const [reviewStep, setReviewStep] = useState(1);
    const [comment, setComment] = useState("");
    const [assignTo, setAssignTo] = useState("");
    const [physicianComment, setPhysicianComment] = useState("");
    const [errors, setErrors] = useState({
        comment: "",
        assignTo: "",
        physicianComment: "",
    });

    const [adminList, setAdminList] = useState([]);
    // const [step1Loading, setStep1Loading] = useState(false);

    const loadAdminMembers = async () => {
        try {
            const { data } = await axios.get(`/team/admins`);
            setAdminList(
                data.data
                    .filter((x) => x.role === "COACH")
                    ?.map((item) => ({
                        value: item.id,
                        label: `${item.first_name} ${item.last_name}`,
                    })),
            );
        } catch (error) {
            setAlertDetails({
                showAlert: true,
                severity: "error",
                message: "Error loading coach members",
            });
        }
    };

    useEffect(() => {
        loadAdminMembers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCommentChange = (value) => {
        setComment(value);
        if (errors.comment) setErrors((e) => ({ ...e, comment: "" }));
    };
    const handleAssignToChange = (value) => {
        setAssignTo(value);
        if (errors.assignTo) setErrors((e) => ({ ...e, assignTo: "" }));
    };
    const handlePhysicianCommentChange = (value) => {
        setPhysicianComment(value);
        if (errors.physicianComment)
            setErrors((e) => ({ ...e, physicianComment: "" }));
    };

    const deviceName =
        reportData?.device_name ??
        (formData?.device && devices?.length
            ? (devices.find((d) => d.value === formData.device)?.label ??
              formData.device)
            : "OxiWear 001");
    const startValue = formData
        ? formatDateTimeColon(formData.startDate, formData.startTime)
        : DUMMY_START;
    const endValue = formData
        ? formatDateTimeColon(formData.endDate, formData.endTime)
        : DUMMY_END;
    const durationHMS = formData
        ? getSleepDurationHMS(
              formData.startDate,
              formData.startTime,
              formData.endDate,
              formData.endTime,
          )
        : DUMMY_DURATION_HMS;
    const duration = durationHMS || DUMMY_DURATION_HMS;

    return (
        <div>
            <SleepSummaryStep
                // step={reviewStep}
                deviceName={deviceName}
                durationHours={duration.hours}
                durationMinutes={duration.minutes}
                durationSeconds={duration.seconds}
                startValue={startValue}
                endValue={endValue}
                reportData={reportData}
                dropEpisodes={
                    dropEpisodes != null ? dropEpisodes : SAMPLE_DROP_EPISODES
                }
                comment={comment}
                onCommentChange={handleCommentChange}
                assignTo={assignTo}
                onAssignToChange={handleAssignToChange}
                commentError={errors.comment}
                assignToError={errors.assignTo}
                physicianComment={physicianComment}
                onPhysicianCommentChange={handlePhysicianCommentChange}
                physicianCommentError={errors.physicianComment}
                adminList={adminList}
                reportId={reportId}
                setReportGeneratedSuccessAlert={setReportGeneratedSuccessAlert}
                setSleepReportModalOpen={setSleepReportModalOpen}
                loadWaitingData={loadWaitingData}
                onSleepReportGenerated={onSleepReportGenerated}
                onBack={onBack}
            />
        </div>
    );
}
